import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import { pauseEmbed } from '../../utils/pauseEmbed';

type Result = ModelHookResult<{
	iframeRef: React.RefObject<HTMLIFrameElement | null>;
	/** Keyed into the embed: bumping it remounts the iframe. */
	embedKey: number;
}>;

/**
 * Stops playback in a slide's embed when the slide stops being active: the player is paused in place
 * when it supports that, and only an embed without a known control protocol is remounted.
 */
export function useEmbedPlayback({ isActive, embedUrl }: { isActive: boolean; embedUrl: string }): Result {
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const [embedKey, setEmbedKey] = useState(0);
	const wasActiveRef = useRef(isActive);

	useEffect(() => {
		const wasActive = wasActiveRef.current;
		wasActiveRef.current = isActive;
		// only an active → inactive change stops playback, so re-runs without one (StrictMode's extra
		// mount run, a new embed URL) leave the player alone
		if (!wasActive || isActive) return;

		const iframe = iframeRef.current;
		if (iframe && pauseEmbed(iframe, embedUrl)) return;
		setEmbedKey((key) => key + 1);
	}, [isActive, embedUrl]);

	return { data: { iframeRef, embedKey } };
}
